import { useEffect, useState } from "react";
import {
  WEEKDAYS,
  createSlot,
  updateSlot,
  type AvailabilitySlot,
} from "@/lib/availability";
import { CALENDAR_STATUS_META, CALENDAR_TARGETS } from "@/pages/admin/calendar/calendarUtils";

interface SlotModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing?: AvailabilitySlot | null;
}

export default function SlotModal({ open, onClose, onSaved, editing }: SlotModalProps) {
  const [scope, setScope] = useState("farm");
  const [target, setTarget] = useState(CALENDAR_TARGETS.farm[0]);
  const [weekday, setWeekday] = useState(6);
  const [startTime, setStartTime] = useState("10:00 AM");
  const [endTime, setEndTime] = useState("02:00 PM");
  const [capacity, setCapacity] = useState(15);
  const [minGuests, setMinGuests] = useState(2);
  const [maxGuests, setMaxGuests] = useState(15);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editing) {
      setScope(editing.scope);
      setTarget(editing.target || CALENDAR_TARGETS[editing.scope][0]);
      setWeekday(editing.weekday);
      setStartTime(editing.start_time);
      setEndTime(editing.end_time);
      setCapacity(editing.capacity);
      setMinGuests(editing.min_guests);
      setMaxGuests(editing.max_guests);
      setActive(editing.active);
    } else {
      setScope("farm");
      setTarget(CALENDAR_TARGETS.farm[0]);
      setWeekday(6);
      setStartTime("10:00 AM");
      setEndTime("02:00 PM");
      setCapacity(15);
      setMinGuests(2);
      setMaxGuests(15);
      setActive(true);
    }
  }, [open, editing]);

  useEffect(() => {
    if (open && !editing) setTarget(CALENDAR_TARGETS[scope][0]);
  }, [scope, open, editing]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!startTime.trim() || !endTime.trim()) {
      setError("Please enter a start and end time.");
      return;
    }
    if (minGuests > maxGuests) {
      setError("Minimum guests can't be greater than maximum guests.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      scope,
      target,
      weekday,
      start_time: startTime.trim(),
      end_time: endTime.trim(),
      capacity,
      min_guests: minGuests,
      max_guests: maxGuests,
      active,
    };
    try {
      if (editing) await updateSlot(editing.id, payload);
      else await createSlot(payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't save the slot.");
    } finally {
      setSaving(false);
    }
  };

  const field =
    "w-full bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400";
  const labelCls = "block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-1.5";

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60" onClick={onClose} aria-hidden="true"></div>
      <div
        className="relative w-full max-w-lg bg-background-50 border border-background-200 rounded-lg p-5 md:p-6 max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={editing ? "Edit slot" : "Add slot"}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-foreground-950 font-semibold">
              {editing ? "Edit time slot" : "Add time slot"}
            </h2>
            <p className="text-sm text-foreground-600 mt-1">
              Set recurring weekly availability for a farm or experience.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {error ? (
          <p className="flex items-start gap-2 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-md px-3 py-2.5 mt-4">
            <i className="ri-error-warning-line text-lg"></i>
            {error}
          </p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <label>
            <span className={labelCls}>Scope</span>
            <select value={scope} onChange={(event) => setScope(event.target.value)} className={field}>
              <option value="farm">Farm</option>
              <option value="experience">Experience</option>
            </select>
          </label>
          <label>
            <span className={labelCls}>Target</span>
            <select value={target} onChange={(event) => setTarget(event.target.value)} className={field}>
              {CALENDAR_TARGETS[scope].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Weekday</span>
            <select value={weekday} onChange={(event) => setWeekday(Number(event.target.value))} className={field}>
              {WEEKDAYS.map((day, index) => (
                <option key={day} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className={labelCls}>Start time</span>
            <input type="text" value={startTime} onChange={(event) => setStartTime(event.target.value)} className={field} placeholder="10:00 AM" />
          </label>
          <label>
            <span className={labelCls}>End time</span>
            <input type="text" value={endTime} onChange={(event) => setEndTime(event.target.value)} className={field} placeholder="02:00 PM" />
          </label>
          <label>
            <span className={labelCls}>Capacity</span>
            <input type="number" min={1} value={capacity} onChange={(event) => setCapacity(Number(event.target.value))} className={field} />
          </label>
          <label className="flex items-end">
            <span className="flex items-center gap-2 text-sm text-foreground-700 pb-3">
              <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} className="w-4 h-4 accent-[oklch(var(--primary-500))]" />
              Slot is active
            </span>
          </label>
          <label>
            <span className={labelCls}>Min guests</span>
            <input type="number" min={1} value={minGuests} onChange={(event) => setMinGuests(Number(event.target.value))} className={field} />
          </label>
          <label>
            <span className={labelCls}>Max guests</span>
            <input type="number" min={1} value={maxGuests} onChange={(event) => setMaxGuests(Number(event.target.value))} className={field} />
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-foreground-700 px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
          >
            <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"}></i>
            {saving ? "Saving…" : editing ? "Save changes" : "Add slot"}
          </button>
        </div>

        <p className="text-[11px] text-foreground-400 mt-4 flex items-center gap-1.5">
          <i className="ri-information-line"></i>
          Guest-facing booking calendar sync · {CALENDAR_STATUS_META.available.label} slots appear bookable.
        </p>
      </div>
    </div>
  );
}